import React, { useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'utils/i18n';
import { DOCS_PAGES, getCurrentDocsPage } from './docsPagesConfig';
import { verticalTabs, colors } from './docsStyles';

/**
 * DocsNavigation - Vertical tab navigation for docs pages
 * Displays as a vertical list in the left sidebar (desktop) or dropdown (mobile)
 * Props:
 * - mobileOnly: if true, only show mobile dropdown; if false, only show desktop navigation
 */
const DocsNavigation = ({ mobileOnly = null }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();

  // Get current docs page
  const currentPage = useMemo(() => {
    return getCurrentDocsPage(location.pathname);
  }, [location.pathname]);

  // Get language prefix from current path
  const langPrefix = useMemo(() => {
    const match = location.pathname.match(/^\/([a-z]{2})\//);
    return match ? `/${match[1]}` : '';
  }, [location.pathname]);

  // Navigate to docs page
  const handleTabClick = (page) => {
    const targetPath = langPrefix ? `${langPrefix}${page.path}` : page.path;
    navigate(targetPath);
  };

  // Check if a page is active
  const isActive = (page) => {
    return currentPage && currentPage.id === page.id;
  };

  // Get translated label
  const getLabel = (page) => {
    // Try to get translation, fallback to label
    const translated = t('title', page.translationKey);
    return translated && translated !== page.label ? translated : page.label;
  };

  const containerStyle = {
    ...verticalTabs.container
  };

  const getTabStyle = (active) => ({
    ...verticalTabs.tab,
    ...(active ? verticalTabs.tabActive : {})
  });

  // Mobile dropdown handler
  const handleMobileSelect = (e) => {
    const selectedPage = DOCS_PAGES.find(p => p.id === e.target.value);
    if (selectedPage) {
      handleTabClick(selectedPage);
    }
  };

  // If mobileOnly is explicitly set, show only that version
  if (mobileOnly === true) {
    return (
      <div className="docs-navigation-mobile">
        <select
          value={currentPage?.id || ''}
          onChange={handleMobileSelect}
          aria-label="Select document"
          className="docs-nav-dropdown"
        >
          {DOCS_PAGES.map((page) => (
            <option key={page.id} value={page.id}>
              {getLabel(page)}
            </option>
          ))}
        </select>
      </div>
    );
  }

  if (mobileOnly === false) {
    return (
      <nav 
        className="docs-navigation-vertical docs-navigation-desktop" 
        aria-label="Documents navigation"
        style={containerStyle}
      >
        {DOCS_PAGES.map((page) => {
          const active = isActive(page);
          return (
            <button
              key={page.id}
              onClick={() => handleTabClick(page)}
              aria-current={active ? 'page' : undefined}
              className={`docs-nav-tab-vertical ${active ? 'active' : ''}`}
              style={getTabStyle(active)}
              onMouseEnter={(e) => {
                if (!active) {
                  e.target.style.backgroundColor = colors.backgroundHover;
                  e.target.style.color = colors.text;
                }
              }}
              onMouseLeave={(e) => {
                if (!active) {
                  e.target.style.backgroundColor = 'transparent';
                  e.target.style.color = colors.textSecondary;
                }
              }}
            >
              {getLabel(page)}
            </button>
          );
        })}
      </nav>
    );
  }

  // Default: Show both, CSS will handle visibility
  return (
    <>
      {/* Desktop: Vertical tab navigation */}
      <nav 
        className="docs-navigation-vertical docs-navigation-desktop" 
        aria-label="Documents navigation"
        style={containerStyle}
      >
        {DOCS_PAGES.map((page) => {
          const active = isActive(page);
          return (
            <button
              key={page.id}
              onClick={() => handleTabClick(page)}
              aria-current={active ? 'page' : undefined}
              className={`docs-nav-tab-vertical ${active ? 'active' : ''}`}
              style={getTabStyle(active)}
              onMouseEnter={(e) => {
                if (!active) {
                  e.target.style.backgroundColor = colors.backgroundHover;
                  e.target.style.color = colors.text;
                }
              }}
              onMouseLeave={(e) => {
                if (!active) {
                  e.target.style.backgroundColor = 'transparent';
                  e.target.style.color = colors.textSecondary;
                }
              }}
            >
              {getLabel(page)}
            </button>
          );
        })}
      </nav>

      {/* Mobile: Dropdown select */}
      <div className="docs-navigation-mobile">
        <select
          value={currentPage?.id || ''}
          onChange={handleMobileSelect}
          aria-label="Select document"
          className="docs-nav-dropdown"
        >
          {DOCS_PAGES.map((page) => (
            <option key={page.id} value={page.id}>
              {getLabel(page)}
            </option>
          ))}
        </select>
      </div>
    </>
  );
};

export default DocsNavigation;




